// RITA-1525 (Teilpaket 2) — Positionen und Auswahl im Vertragsdokument.
//
// Eine Position zeigt auf einen Bereich (Text, Kopf- oder Fußzeile), einen Block, einen
// Listeneintrag (bei Absätzen 0) und einen Offset in Einheiten: Textzeichen zählen einzeln,
// Platzhalter, Kästchen und Zeilenumbrüche je genau eine Einheit. So bleiben Platzhalter und
// Kästchen unteilbar – der Schutz aus RITA-1475 ergibt sich aus der Struktur. Alles hier ist rein
// (kein DOM); die Zuordnung zur DOM-Auswahl liegt in editor/domAuswahl.ts.
import { Block, Inlineknoten, Platzhalterknoten, Textlauf, Vertragsdokument, Zeilenblock } from './types';

export type Bereich = 'text' | 'kopf' | 'fuss';

export interface Position {
  bereich: Bereich;
  blockId: string;
  /** Index des Listeneintrags; bei Absätzen immer 0. */
  eintrag: number;
  offset: number;
}

export interface Auswahl {
  anker: Position;
  fokus: Position;
}

export interface Formatmerkmale {
  fett: boolean;
  kursiv: boolean;
  unterstrichen: boolean;
  /** Eigene Schriftgröße in Punkt (RITA-1534); fehlt sie, gilt die Grundgröße des Bereichs. */
  groessePt?: number;
}

export const OHNE_FORMAT: Formatmerkmale = { fett: false, kursiv: false, unterstrichen: false };

export const BEREICHE: readonly Bereich[] = ['kopf', 'text', 'fuss'];

export function position(bereich: Bereich, blockId: string, offset: number, eintrag = 0): Position {
  return { bereich, blockId, eintrag, offset };
}

export function kollabiert(stelle: Position): Auswahl {
  return { anker: stelle, fokus: stelle };
}

export function positionenGleich(a: Position, b: Position): boolean {
  return a.bereich === b.bereich && a.blockId === b.blockId && a.eintrag === b.eintrag && a.offset === b.offset;
}

export function auswahlenGleich(a: Auswahl | null, b: Auswahl | null): boolean {
  if (a === null || b === null) return a === b;
  return positionenGleich(a.anker, b.anker) && positionenGleich(a.fokus, b.fokus);
}

export function istKollabiert(auswahl: Auswahl): boolean {
  return positionenGleich(auswahl.anker, auswahl.fokus);
}

export function bloeckeVon(dokument: Vertragsdokument, bereich: Bereich): Block[] {
  if (bereich === 'text') return dokument.text;
  return bereich === 'kopf' ? dokument.kopf.inhalt : dokument.fuss.inhalt;
}

/** Liefert ein neues Dokument, in dem die Blöcke des Bereichs ersetzt sind (Eingabe bleibt unverändert). */
export function mitBloecken(dokument: Vertragsdokument, bereich: Bereich, bloecke: Block[]): Vertragsdokument {
  if (bereich === 'text') return { ...dokument, text: bloecke };
  const zeilen = bloecke.filter((block): block is Zeilenblock => block.typ !== 'seitenumbruch');
  if (bereich === 'kopf') return { ...dokument, kopf: { ...dokument.kopf, inhalt: zeilen } };
  return { ...dokument, fuss: { ...dokument.fuss, inhalt: zeilen } };
}

export function knotenLaenge(knoten: Inlineknoten): number {
  return knoten.typ === 'text' ? knoten.text.length : 1;
}

export function inhaltLaenge(inhalt: Inlineknoten[]): number {
  return inhalt.reduce((summe, knoten) => summe + knotenLaenge(knoten), 0);
}

/** Inline-Inhalt eines Blocks; `null` bei Seitenumbrüchen und ungültigem Eintrag. */
export function inhaltVon(block: Block, eintrag: number): Inlineknoten[] | null {
  if (block.typ === 'absatz') return eintrag === 0 ? block.inhalt : null;
  if (block.typ === 'liste') return block.eintraege[eintrag] ?? null;
  return null;
}

export function anzahlEintraege(block: Block): number {
  if (block.typ === 'absatz') return 1;
  if (block.typ === 'liste') return block.eintraege.length;
  return 0;
}

export function blockLaenge(block: Block, eintrag: number): number {
  const inhalt = inhaltVon(block, eintrag);
  return inhalt ? inhaltLaenge(inhalt) : 0;
}

export function blockIndex(dokument: Vertragsdokument, bereich: Bereich, blockId: string): number {
  return bloeckeVon(dokument, bereich).findIndex((block) => block.id === blockId);
}

export function blockVon(dokument: Vertragsdokument, stelle: Position): Block | null {
  return bloeckeVon(dokument, stelle.bereich).find((block) => block.id === stelle.blockId) ?? null;
}

export function inhaltAn(dokument: Vertragsdokument, stelle: Position): Inlineknoten[] | null {
  const block = blockVon(dokument, stelle);
  return block ? inhaltVon(block, stelle.eintrag) : null;
}

/** Dokumentreihenfolge: Kopf vor Text vor Fuß, dann Block, Eintrag, Offset. Negativ, wenn a vor b liegt. */
export function vergleichePositionen(dokument: Vertragsdokument, a: Position, b: Position): number {
  if (a.bereich !== b.bereich) return BEREICHE.indexOf(a.bereich) - BEREICHE.indexOf(b.bereich);
  if (a.blockId !== b.blockId) {
    return blockIndex(dokument, a.bereich, a.blockId) - blockIndex(dokument, b.bereich, b.blockId);
  }
  if (a.eintrag !== b.eintrag) return a.eintrag - b.eintrag;
  return a.offset - b.offset;
}

/** [Anfang, Ende] in Dokumentreihenfolge, unabhängig von der Richtung der Auswahl. */
export function auswahlSortiert(dokument: Vertragsdokument, auswahl: Auswahl): [Position, Position] {
  return vergleichePositionen(dokument, auswahl.anker, auswahl.fokus) <= 0
    ? [auswahl.anker, auswahl.fokus]
    : [auswahl.fokus, auswahl.anker];
}

function endeDesBlocks(block: Block): Position | null {
  const eintrag = anzahlEintraege(block) - 1;
  if (eintrag < 0) return null;
  return position('text', block.id, blockLaenge(block, eintrag), eintrag);
}

/**
 * Klemmt eine Position auf gültige Werte: unbekannter Block → Bereichsanfang (bzw. `null`, wenn der
 * Bereich keinen Block mit Inhalt hat), Eintrag und Offset in den Grenzen. Seitenumbrüche tragen
 * keine Position; die Position wandert an den Anfang des Folgeblocks (am Ende: Ende des Vorblocks).
 */
export function positionKlemmen(dokument: Vertragsdokument, stelle: Position): Position | null {
  const bloecke = bloeckeVon(dokument, stelle.bereich);
  if (bloecke.length === 0) return null;
  let index = bloecke.findIndex((block) => block.id === stelle.blockId);
  if (index < 0) index = 0;
  const block = bloecke[index];
  if (block.typ === 'seitenumbruch') {
    const folgender = bloecke.slice(index + 1).find((kandidat) => kandidat.typ !== 'seitenumbruch');
    if (folgender) return position(stelle.bereich, folgender.id, 0);
    const vorheriger = bloecke
      .slice(0, index)
      .reverse()
      .find((kandidat) => kandidat.typ !== 'seitenumbruch');
    if (!vorheriger) return null;
    const ende = endeDesBlocks(vorheriger);
    return ende ? { ...ende, bereich: stelle.bereich } : null;
  }
  const eintrag = Math.min(Math.max(0, stelle.eintrag), anzahlEintraege(block) - 1);
  const offset = Math.min(Math.max(0, stelle.offset), blockLaenge(block, eintrag));
  return position(stelle.bereich, block.id, offset, eintrag);
}

/** Klemmt beide Enden; ein Fokus in einem anderen Bereich als der Anker wird auf den Anker gesetzt. */
export function auswahlKlemmen(dokument: Vertragsdokument, auswahl: Auswahl): Auswahl | null {
  const anker = positionKlemmen(dokument, auswahl.anker);
  if (!anker) return null;
  const fokus = auswahl.fokus.bereich === anker.bereich ? positionKlemmen(dokument, auswahl.fokus) : anker;
  return { anker, fokus: fokus ?? anker };
}

/**
 * Eine nicht kollabierte Auswahl, die bei Offset 0 eines späteren Blocks oder Eintrags endet
 * (Dreifachklick in Chromium), endet stattdessen am Ende des vorherigen Blocks bzw. Eintrags.
 * So ersetzt Tippen oder Formatieren nur den markierten Block und zieht den Folgeblock nicht hinein.
 */
export function auswahlEntHaengen(dokument: Vertragsdokument, auswahl: Auswahl): Auswahl {
  if (istKollabiert(auswahl)) return auswahl;
  const [start, ende] = auswahlSortiert(dokument, auswahl);
  if (ende.offset !== 0) return auswahl;
  if (ende.blockId === start.blockId && ende.eintrag === start.eintrag) return auswahl;
  const bloecke = bloeckeVon(dokument, ende.bereich);
  const index = bloecke.findIndex((block) => block.id === ende.blockId);
  const block = bloecke[index];
  let neuesEnde: Position | null = null;
  if (block && block.typ === 'liste' && ende.eintrag > 0) {
    neuesEnde = position(ende.bereich, block.id, blockLaenge(block, ende.eintrag - 1), ende.eintrag - 1);
  } else {
    const vorheriger = bloecke
      .slice(0, Math.max(0, index))
      .reverse()
      .find((kandidat) => kandidat.typ !== 'seitenumbruch');
    const blockende = vorheriger ? endeDesBlocks(vorheriger) : null;
    neuesEnde = blockende ? { ...blockende, bereich: ende.bereich } : null;
  }
  if (!neuesEnde || vergleichePositionen(dokument, neuesEnde, start) < 0) return auswahl;
  return auswahl.anker === start ? { anker: start, fokus: neuesEnde } : { anker: neuesEnde, fokus: start };
}

export interface Knotenstelle {
  /** Index des Inline-Knotens, in dem der Offset liegt; 0, wenn der Inhalt leer ist. */
  index: number;
  /** Offset innerhalb des Knotens (bei atomaren Knoten 0 = davor, 1 = danach). */
  innen: number;
}

/**
 * Ordnet einen Offset dem Inline-Knoten zu. An einer Grenze zwischen zwei Knoten gehört der Offset
 * zum vorherigen Knoten (Ende), damit Eingaben dessen Format erben; nur am Anfang (Offset 0) zum
 * ersten Knoten.
 */
export function stelleAnOffset(inhalt: Inlineknoten[], offset: number): Knotenstelle {
  if (inhalt.length === 0) return { index: 0, innen: 0 };
  let anfang = 0;
  for (let index = 0; index < inhalt.length; index += 1) {
    const laenge = knotenLaenge(inhalt[index]);
    if (offset <= anfang + laenge && (offset > anfang || index === 0)) {
      return { index, innen: offset - anfang };
    }
    anfang += laenge;
  }
  const letzter = inhalt.length - 1;
  return { index: letzter, innen: knotenLaenge(inhalt[letzter]) };
}

/** Knoten mit Zeichenformat: Textläufe und (seit RITA-1533) Platzhalter; Kästchen und Zeilenumbrüche nicht. */
export type Formattraeger = Textlauf | Platzhalterknoten;

export function istFormattraeger(knoten: Inlineknoten): knoten is Formattraeger {
  return knoten.typ === 'text' || knoten.typ === 'platzhalter';
}

/** Dasselbe Format mit dieser eigenen Größe; `undefined` entfernt die eigene Größe (RITA-1534). */
export function mitGroesse(format: Formatmerkmale, groessePt: number | undefined): Formatmerkmale {
  const ergebnis: Formatmerkmale = { fett: format.fett, kursiv: format.kursiv, unterstrichen: format.unterstrichen };
  if (groessePt !== undefined) ergebnis.groessePt = groessePt;
  return ergebnis;
}

export function formatVon(knoten: Formattraeger): Formatmerkmale {
  return mitGroesse(
    { fett: !!knoten.fett, kursiv: !!knoten.kursiv, unterstrichen: !!knoten.unterstrichen },
    knoten.groessePt,
  );
}

/** Gleich sind Formate nur mit derselben eigenen Größe: Läufe verschiedener Größe verschmelzen nie (RITA-1534). */
export function formateGleich(a: Formatmerkmale, b: Formatmerkmale): boolean {
  return (
    a.fett === b.fett && a.kursiv === b.kursiv && a.unterstrichen === b.unterstrichen && a.groessePt === b.groessePt
  );
}

/**
 * Format, das eine Eingabe an dieser Stelle erbt: der Formatträger (Textlauf oder Platzhalter), in dem
 * der Caret steht bzw. der unmittelbar davor endet. Ein Platzhalter verhält sich dabei wie ein Zeichen
 * Text (RITA-1533): Hinter ihm gilt sein eigenes Format. Hinter einem Kästchen zählt der nächste
 * Formatträger davor in derselben Zeile, sonst der nächste danach in derselben Zeile; am Anfang einer
 * Zeile (nach einem Zeilenumbruch, am Blockanfang) der erste Formatträger dieser Zeile. Sonst `null`: kein
 * Formatträger bestimmt die Stelle (leerer Block, Zeilenende hinter einem Zeilenumbruch, nur Kästchen) – der
 * Editor führt dann das Format der Ausgangsstelle über Enter bzw. Umschalt+Enter weiter (RITA-1534, !768).
 */
export function geerbtesFormatAnStelle(inhalt: Inlineknoten[], offset: number): Formatmerkmale | null {
  if (inhalt.length === 0) return null;
  const stelle = stelleAnOffset(inhalt, offset);
  const knoten = inhalt[stelle.index];
  if (!knoten) return null;
  if (istFormattraeger(knoten)) return formatVon(knoten);
  const rueckwaerts = (von: number): Formatmerkmale | null => {
    for (let index = von; index >= 0; index -= 1) {
      const kandidat = inhalt[index];
      if (kandidat.typ === 'zeilenumbruch') return null;
      if (istFormattraeger(kandidat)) return formatVon(kandidat);
    }
    return null;
  };
  const vorwaerts = (von: number): Formatmerkmale | null => {
    for (let index = von; index < inhalt.length; index += 1) {
      const kandidat = inhalt[index];
      if (kandidat.typ === 'zeilenumbruch') return null;
      if (istFormattraeger(kandidat)) return formatVon(kandidat);
    }
    return null;
  };
  if (stelle.innen === 0) return vorwaerts(stelle.index);
  if (knoten.typ === 'zeilenumbruch') return vorwaerts(stelle.index + 1);
  return rueckwaerts(stelle.index - 1) ?? vorwaerts(stelle.index + 1);
}

/** Wie `geerbtesFormatAnStelle`, aber „kein Format“ statt `null`, wenn kein Formatträger die Stelle bestimmt. */
export function formatAnStelle(inhalt: Inlineknoten[], offset: number): Formatmerkmale {
  return geerbtesFormatAnStelle(inhalt, offset) ?? OHNE_FORMAT;
}

/** `null`, wenn kein Formatträger die Stelle bestimmt (siehe `geerbtesFormatAnStelle`). */
export function geerbtesFormatVorPosition(dokument: Vertragsdokument, stelle: Position): Formatmerkmale | null {
  const inhalt = inhaltAn(dokument, stelle);
  return inhalt ? geerbtesFormatAnStelle(inhalt, stelle.offset) : null;
}

export function formatVorPosition(dokument: Vertragsdokument, stelle: Position): Formatmerkmale {
  return geerbtesFormatVorPosition(dokument, stelle) ?? OHNE_FORMAT;
}

/** Erste bzw. letzte gültige Position eines Bereichs (`null`, wenn der Bereich keinen Inhalt hat). */
export function anfangVon(dokument: Vertragsdokument, bereich: Bereich): Position | null {
  const block = bloeckeVon(dokument, bereich).find((kandidat) => kandidat.typ !== 'seitenumbruch');
  return block ? position(bereich, block.id, 0) : null;
}

export function endeVon(dokument: Vertragsdokument, bereich: Bereich): Position | null {
  const block = [...bloeckeVon(dokument, bereich)].reverse().find((kandidat) => kandidat.typ !== 'seitenumbruch');
  if (!block) return null;
  const ende = endeDesBlocks(block);
  return ende ? { ...ende, bereich } : null;
}
